import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  PlayCircleIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { getDefaultGuidesHome, resolveLinkedGuide, resolveStepMainGuide } from '../../utils/vaGuidesHome';
import VAGuideStepsPanel from './VAGuideStepsPanel';

function GuideLinkButton({ guide, label, activeGuideId, onToggleGuide }) {
  if (!guide) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300">
        <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
        Guide not published yet — ask your manager
      </span>
    );
  }

  const guideId = String(guide._id);
  const isOpen = activeGuideId === guideId;

  return (
    <button
      type="button"
      onClick={() => onToggleGuide(isOpen ? null : guide)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium shadow-sm transition-colors ${
        isOpen
          ? 'bg-indigo-800 text-white'
          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
      }`}
    >
      <BookOpenIcon className="w-4 h-4" />
      {isOpen ? 'Close guide' : `Open guide: ${label || guide.title}`}
      {!isOpen && <ArrowRightIcon className="w-4 h-4" />}
    </button>
  );
}

function renderOnboardingStep(item, guides, activeGuideId, onToggleGuide) {
  const mainGuide = resolveStepMainGuide(guides, item);

  return (
    <div
      key={item._id || item.step}
      className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm"
    >
      <div className="flex gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold">
          {item.step}
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white">{item.title}</h4>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{item.summary}</p>
            {item.important && (
              <p className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {item.important}
              </p>
            )}
          </div>

          {item.portalPath && (
            <Link
              to={item.portalPath}
              className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {item.portalLabel}
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          )}

          {(item.guideId || item.guideMatchers?.length > 0) && (
            <>
              <GuideLinkButton
                guide={mainGuide}
                label={item.guideLabel}
                activeGuideId={activeGuideId}
                onToggleGuide={onToggleGuide}
              />
              {mainGuide && activeGuideId === String(mainGuide._id) && (
                <VAGuideStepsPanel
                  guide={mainGuide}
                  onClose={() => onToggleGuide(null)}
                />
              )}
            </>
          )}

          {item.linkedGuides?.length > 0 && (
            <div className="space-y-3 pt-1">
              {item.categoryHint && (
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {item.categoryHint} — open each guide:
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {item.linkedGuides.map((sub) => {
                  const subGuide = resolveLinkedGuide(guides, sub);
                  return (
                    <GuideLinkButton
                      key={sub._id || sub.label}
                      guide={subGuide}
                      label={sub.label}
                      activeGuideId={activeGuideId}
                      onToggleGuide={onToggleGuide}
                    />
                  );
                })}
              </div>
              {item.linkedGuides.map((sub) => {
                const subGuide = resolveLinkedGuide(guides, sub);
                if (!subGuide || activeGuideId !== String(subGuide._id)) return null;
                return (
                  <VAGuideStepsPanel
                    key={`panel-${sub._id || sub.label}`}
                    guide={subGuide}
                    onClose={() => onToggleGuide(null)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const VAGuidesHome = ({ guides, guidesHome: guidesHomeProp }) => {
  const [activeGuide, setActiveGuide] = useState(null);
  const activeGuideId = activeGuide ? String(activeGuide._id) : null;
  const home = guidesHomeProp || getDefaultGuidesHome();

  const handleToggleGuide = (guide) => {
    setActiveGuide(guide);
  };

  const sections = [...(home.sections || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const dailyWork = home.dailyWork || getDefaultGuidesHome().dailyWork;

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-indigo-200/80 dark:border-indigo-800/50 bg-gradient-to-br from-indigo-50/90 via-white to-violet-50/50 dark:from-indigo-950/40 dark:via-gray-800 dark:to-violet-950/20 p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-white flex items-center justify-center">
            <PlayCircleIcon className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">{home.intro?.title}</h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{home.intro?.subtitle}</p>
            <p className="mt-3 text-sm font-medium text-indigo-800 dark:text-indigo-200">{home.intro?.note}</p>
          </div>
        </div>
      </section>

      {sections.map((section, sectionIdx) => (
        <section key={section._id || `section-${sectionIdx}`} className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex flex-wrap items-center gap-x-2 gap-y-1">
            <ClipboardDocumentListIcon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
            <span>{section.title}</span>
            {section.highlight && (
              <>
                <span className="text-indigo-600 dark:text-indigo-400">›</span>
                <span className="text-amber-700 dark:text-amber-300">{section.highlight}</span>
              </>
            )}
            {section.stepsLabel && (
              <span className="text-sm font-normal text-slate-500">({section.stepsLabel})</span>
            )}
          </h3>

          <div className="space-y-4">
            {(section.steps || []).map((item) => renderOnboardingStep(item, guides, activeGuideId, handleToggleGuide))}
          </div>
        </section>
      ))}

      <div className="rounded-2xl border-2 border-amber-400 dark:border-amber-500 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/50 dark:via-orange-950/30 dark:to-amber-950/50 px-5 py-5 shadow-md">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-sm">
            <ShieldExclamationIcon className="w-7 h-7" />
          </div>
          <div>
            <p className="text-lg font-bold uppercase tracking-wide text-amber-900 dark:text-amber-100">
              {home.managerGate?.title}
            </p>
            <p className="mt-2 text-sm sm:text-base font-medium text-amber-950 dark:text-amber-50 leading-relaxed">
              {home.managerGate?.message}
            </p>
          </div>
        </div>
      </div>

      <section className="rounded-2xl border border-emerald-200/80 dark:border-emerald-800/50 bg-emerald-50/50 dark:bg-emerald-950/20 p-6 space-y-5">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{dailyWork.title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{dailyWork.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl bg-white/80 dark:bg-gray-800/80 border border-emerald-200/60 dark:border-emerald-800/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300 mb-2">Overview</p>
            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
              {(dailyWork.overview || []).map((line) => (
                <li key={line} className="flex gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl bg-white/80 dark:bg-gray-800/80 border border-emerald-200/60 dark:border-emerald-800/40 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300 mb-2">Important rules</p>
            <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
              {(dailyWork.rules || []).map((line) => (
                <li key={line} className="flex gap-2">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-xl bg-white/80 dark:bg-gray-800/80 border border-emerald-200/60 dark:border-emerald-800/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300 mb-3">
            Daily following process
          </p>
          <ol className="space-y-2 text-sm text-slate-700 dark:text-slate-300 list-decimal list-inside">
            {(dailyWork.process || []).map((line) => (
              <li key={line} className="leading-relaxed">{line}</li>
            ))}
          </ol>
        </div>

        <div className="rounded-xl bg-emerald-600 text-white px-4 py-3 text-center">
          <p className="text-xs uppercase tracking-wide opacity-90">{dailyWork.target?.label}</p>
          <p className="text-lg font-bold mt-1">{dailyWork.target?.math}</p>
        </div>

        <div className="rounded-xl bg-white/80 dark:bg-gray-800/80 border border-emerald-200/60 dark:border-emerald-800/40 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300 mb-2">Tips</p>
          <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
            {(dailyWork.tips || []).map((line) => (
              <li key={line}>— {line}</li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default VAGuidesHome;
